import angular from 'angular'
import { ApiCustomer } from '@vend/types'
import type { ModalFactory } from '../vend-ui/types'

import addCustomerTemplate from '../../templates/current-sale/add-customer-to-sale.html'
import viewCustomerTemplate from '../components/react/business-components/CustomerModal/customer-modal.html'
import issueStoreCreditTemplate from '../../templates/customer/issue-store-credit.html'
import editCustomerTemplate from '../components/react/business-components/EditCustomerModal/edit-customer-modal.html'
import { $timeout } from '../ng-compat/imports'
import { ReactModalResult } from '../components/react/ReactModalController'
import { EditCustomerModalResult } from '../components/react/business-components/EditCustomerModal/edit-customer-modal.controller'
import { detectCustomerFieldFromQuery } from '../functions/customer'

/**
 * Provides customer related modals e.g. to allow the user to create/edit/view customers.
 *
 * @module customerModals
 */
angular
  .module('webregister.modals')
  .factory(
    'customerModals',
    function (Modal: ModalFactory, customerManager: $TSFixMe) {
      /**
       * Provides customer related modals e.g. to allow the user to create/edit/view customers.
       *
       * @class CustomerLightboxes
       * @static
       */
      return {
        // ============================== Public API ============================== //

        /**
         * Responsible for showing the edit customer modal instance.
         *
         * @method updateCustomer
         *
         * @param  customer {Customer}
         *         An existing customer instance
         *
         * @return {Promise<Customer>} A promise resolved with the updated customer details.
         */
        updateCustomer(customer: ApiCustomer) {
          return this._openEditCustomer({
            customerId: customer.id,
          })
        },

        /**
         * Responsible for showing the create new customer modal instance.
         *
         * @method createCustomer
         *
         * @param  [options] {Object}
         *
         *         @param [options.query] {String}
         *                The search query accompanying the create customer method.
         *
         * @return {Promise<Customer>} A promise resolved with the newly created customer details.
         */
        createCustomer(options?: { query?: string }) {
          return this._openEditCustomer(options)
        },

        /**
         * Open the Add Customer to Sale lightbox.
         *
         * @method addCustomerToSale
         *
         * @param  [options] {Object}
         *
         *         @param [options.cancelButtonLabel='Cancel'] {String}
         *                Custom label to use for the 'Cancel' button (e.g. 'Skip')
         *
         * @return {Promise} A promise resolved once a customer has been added to the sale or the modal cancelled
         */
        addCustomerToSale(
          options: {
            cancelButtonLabel?: string
          } = {}
        ) {
          return customerManager
            .getCustomerGroups()
            .then((customerGroups: $TSFixMe) => {
              return Modal({
                template: addCustomerTemplate,
                controller: 'AddCustomerToSaleCtrl',
                controllerAs: 'addCustomerToSaleCtrl',
                cancelButtonLabel: options.cancelButtonLabel,
                customerGroups,
              }).then((modal: $TSFixMe) => modal.closed)
            })
        },

        /**
         * Open the View Customer lightbox.
         *
         * @method viewCustomer
         *
         * @param  customer {Customer}
         *         The customer to display in the lightbox
         *
         * @param {Object} viewOptions
         *
         *        @param  [hideRemoveFromSale] {Boolean}
         *                Optional boolean to hide "remove customer from sale"
         *
         *        @param  [hideViewSales] {Boolean}
         *                Optional boolean to hide "view customer sales"
         *
         * @return {ModalInstance} The instance of the modal for the view customer dialog
         */
        viewCustomer(customer: ApiCustomer, viewOptions: $TSFixMe = {}) {
          const { hideEditCustomer = false } = viewOptions

          return Modal({
            controller: 'customerModalController',
            controllerAs: '$ctrl',
            template: viewCustomerTemplate,
            customerId: customer.id,
            showEdit: !hideEditCustomer,
          }).then((modal: $TSFixMe) =>
            modal.closed.then((result: ReactModalResult) => {
              switch (result.action) {
                case 'edit':
                  return $timeout(() => this.updateCustomer(customer))
                case 'issueStoreCredit':
                  return $timeout(() => this.issueStoreCredit(customer))
              }
            })
          )
        },

        /**
         * Open the Issue Store Credit modal.
         *
         * @method issueStoreCredit
         *
         * @param  customer {Customer}
         *         The customer who is to receive the store credit
         *
         * @return {ModalInstance} The instance of the modal for the issue store credit dialog
         */
        issueStoreCredit(customer: ApiCustomer) {
          return Modal({
            controller: 'IssueStoreCreditCtrl',
            controllerAs: 'issueStoreCreditCtrl',
            template: issueStoreCreditTemplate,
            customer,
          })
        },

        // ============================== Private API ============================== //

        /**
         * Responsible for opening the edit customer modal. A shared modal, capable of either editing an existing customer
         * or creating a new one.
         *
         * @private
         * @method _openEditCustomer
         *
         * @param  [options] {Object}
         *
         *         @param [options.customerId] {String}
         *                The id of the customer to edit, or undefined
         *
         *         @param [options.query] {String}
         *                The query to prepopulate the form with
         *
         * @return {Promise<Customer>} A promised resolved with either the newly created customer or the updated customer
         *                             instance.
         */
        _openEditCustomer(
          options: {
            customerId?: string
            query?: string
          } = {}
        ) {
          const {
            email,
            mobile,
            firstName,
            lastName,
          } = detectCustomerFieldFromQuery(options.query || '')

          const customer = {
            id: options.customerId,
            firstName,
            lastName,
            contact: {
              email,
              mobile,
            },
          }

          return Modal({
            controller: 'editCustomerModalController',
            controllerAs: '$ctrl',
            template: editCustomerTemplate,
            customer,
          }).then((modal: $TSFixMe) => {
            return modal.closed.then(
              (result: EditCustomerModalResult) => result.customer
            )
          })
        },
      }
    }
  )
